import React, { useState } from 'react';
import { getFallbackThumbnailUrl, detectVideoService, getServiceDisplayName } from '../../utils/videoServiceDetector';
import { formatDuration } from '../../utils/durationFormatter';

function VideoThumbnailCard({ video, onUpdate, onDelete, isEditing = false, isSelectable = false, isSelected = false, onToggleSelect }) {
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [title, setTitle] = useState(video.title || '');
  const [description, setDescription] = useState(video.description || '');
  const [url] = useState(video.url || '');

  // Use stored thumbnail URL if available, otherwise try to construct from video ID (YouTube only)
  const thumbnailUrl = video.thumbnailUrl || getFallbackThumbnailUrl(video.url);

  const handleSave = () => {
    onUpdate(video.id, { title, description });
    setIsEditingLocal(false);
  };

  const handleCancel = () => {
    setTitle(video.title || '');
    setDescription(video.description || '');
    setIsEditingLocal(false);
  };

  if (isEditingLocal) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden border-2 border-primary-500">
        <div className="p-4">
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Video URL</label>
            <input
              type="url"
              value={url}
              disabled
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-gray-100 text-gray-600 cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">URL cannot be edited. Delete this video and add a new one to change the URL.</p>
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Video title"
            />
          </div>
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Video description"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex-1 px-3 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 text-sm font-medium"
            >
              Save
            </button>
            <button
              onClick={handleCancel}
              className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleCardClick = () => {
    if (isSelectable && onToggleSelect) {
      onToggleSelect(video.id);
    }
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-md overflow-hidden group hover:shadow-lg transition-shadow ${isSelected ? 'ring-2 ring-primary-500 ring-offset-2' : ''} ${isSelectable ? 'cursor-pointer' : ''}`}
      onClick={isSelectable ? handleCardClick : undefined}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-200">
        {/* Selection checkbox */}
        {isSelectable && (
          <div className="absolute top-2 right-2 z-20">
            <input
              type="checkbox"
              checked={isSelected}
              readOnly
              className="w-5 h-5 text-primary-500 border-2 border-gray-300 rounded focus:ring-primary-500 cursor-pointer shadow-md bg-white pointer-events-none"
            />
          </div>
        )}
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={video.description || 'Video thumbnail'}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
            </svg>
          </div>
        )}

        {/* Duration Badge */}
        {video.duration && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-80 text-white px-2 py-1 rounded text-xs font-semibold">
            {formatDuration(video.duration)}
          </div>
        )}

        {/* Hover overlay - hide in selection mode */}
        {isEditing && !isSelectable && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
            <button
              onClick={() => setIsEditingLocal(true)}
              className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
              title="Edit video"
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => onDelete(video.id)}
              className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
              title="Delete video"
            >
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <a
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
              title={`Open on ${getServiceDisplayName(detectVideoService(video.url))}`}
            >
              <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        )}
      </div>

      {/* Title/Description */}
      <div className="p-3">
        <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1" title={video.title || video.description}>
          {video.title || video.description || 'Untitled Video'}
        </p>

        {/* Channel Title (if available) */}
        {video.channelTitle && (
          <p className="text-xs text-gray-500 mb-1">
            {video.channelTitle}
          </p>
        )}

        {video.description && video.title && (
          <p
            className="text-xs text-gray-600 line-clamp-3 leading-relaxed"
            title={video.description}
          >
            {video.description}
          </p>
        )}
      </div>
    </div>
  );
}

export default VideoThumbnailCard;
